<template>
  <div id="select-company">
    <v-row align="center" justify="center">
      <v-col cols="12" md="5">
        <!-- Search Input -->
        <v-text-field
          v-if="companies.length > 10"
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          placeholder="ค้นหาบริษัท..."
          outlined
          dense
          clearable
          class="mb-4"
        ></v-text-field>
        
        <!-- Layout Toggle -->
        <div v-if="companies.length > 10" class="tw-flex tw-justify-end tw-mb-4">
          <v-btn-toggle
            v-model="layoutMode"
            mandatory
            dense
            class="tw-bg-white tw-border tw-border-gray-200"
            color="primary"
          >
            <v-btn value="list" small>
              <v-icon color="primary">mdi-format-list-bulleted</v-icon>
            </v-btn>
            <v-btn value="grid" small>
              <v-icon color="primary">mdi-view-grid</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>
        
        <!-- List Layout -->
        <div v-if="layoutMode === 'list' || companies.length <= 10">
          <v-list-item v-for="company in filteredCompanies" :key="company.companyID" link
                       :to="`${company.companyID}/dashboard`">
            <v-list-item-icon>
              <v-avatar size="30" color="#F5F5F5" class="m-0" style="background-color: #fff;">
                <img v-if="company.company.imageLogoUrl" :src="company.company.imageLogoUrl + '=s' + Math.round(30 * getDeviceRatio())"
                     :alt="company.company.nameTH" style=" object-fit: contain;">
                <span v-else-if="company.company.nameEN"
                      class="edit-text-avatar">{{ company.company.nameEN[0].toUpperCase() }}</span>
                <span v-else/>
              </v-avatar>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ company.company.nameTH }}</v-list-item-title>
            </v-list-item-content>
             <v-list-item-icon>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </div>
        
        <!-- Grid Layout -->
        <div v-else-if="layoutMode === 'grid' && companies.length > 10" class="tw-grid tw-grid-cols-2 tw-gap-4">
          <div v-for="company in filteredCompanies" :key="company.companyID" 
               class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-cursor-pointer tw-transition-all tw-duration-200 hover:tw-shadow-md hover:tw-border-gray-300"
               @click="$router.push(`${company.companyID}/dashboard`)">
            <div class="tw-flex tw-flex-col tw-items-center tw-text-center">
              <v-avatar size="60" color="#F5F5F5" class="tw-mb-3" style="background-color: #fff;">
                <img v-if="company.company.imageLogoUrl" :src="company.company.imageLogoUrl + '=s' + Math.round(60 * getDeviceRatio())"
                     :alt="company.company.nameTH" style="object-fit: contain;">
                <span v-else-if="company.company.nameEN"
                      class="edit-text-avatar tw-text-xl">{{ company.company.nameEN[0].toUpperCase() }}</span>
                <span v-else/>
              </v-avatar>
              <div class="tw-text-sm tw-font-medium tw-text-gray-900 tw-line-clamp-2">{{ company.company.nameTH }}</div>
            </div>
          </div>
        </div>
        
        <!-- Empty states -->
        <div v-if="companies.length === 0" class="tw-text-center tw-p-8">
          <v-icon size="64" color="primary" class="tw-mb-4">mdi-office-building-outline</v-icon>
          <div class="tw-text-xl tw-text-gray-600 tw-mb-2">ไม่มีบริษัท</div>
          <div class="tw-text-sm tw-text-gray-500">คุณยังไม่ได้เพิ่มบริษัทใดๆ</div>
        </div>
        
        <div v-else-if="searchQuery && filteredCompanies.length === 0" class="tw-text-center tw-p-8">
          <v-icon size="64" color="primary" class="tw-mb-4">mdi-magnify</v-icon>
          <div class="tw-text-xl tw-text-gray-600 tw-mb-2">ไม่พบบริษัท</div>
          <div class="tw-text-sm tw-text-gray-500">ไม่พบบริษัทที่ตรงกับคำค้นหา "{{ searchQuery }}"</div>
        </div>
      </v-col>
    </v-row>
    <v-row style="padding-bottom: 100px" class="d-flex justify-center mt-5" >
      <router-link v-bind:to="'/company-add'" class="button-create">
        <button class="tw-mr-5 hover:tw-bg-light-green hover:tw-bg-opacity-10 tw-font-bold tw-py-2 tw-px-6 tw-rounded-lg">
          <img src="/images/icons/icon_add_company.svg" alt="add-company" class="tw-float-left tw-w-5 tw-mr-2"/>
          เพิ่มบริษัทของคุณ
        </button>
      </router-link>
      <router-link v-bind:to="'/company-add-with-code'" class="button-create">
        <button class="tw-mr-5 hover:tw-bg-light-green hover:tw-bg-opacity-10 tw-font-bold tw-py-2 tw-px-6 tw-rounded-lg">
          <img src="/images/icons/icon_add.svg" alt="add-company" class="tw-float-left tw-w-5 tw-mr-2"/>
          เพิ่มบริษัทด้วยโค้ด
        </button>
      </router-link>
    </v-row>
  </div>
</template>

<script>
import {mapState} from "vuex";

export default {
  name: "selectCompany",
  data() {
    return {
      searchQuery: '',
      layoutMode: 'list'
    }
  },
  computed: {
    ...mapState({
      companies: state => state.companies.list,
    }),
    filteredCompanies() {
      // If 10 or fewer companies, show all companies regardless of search
      if (this.companies.length <= 10) {
        return this.companies;
      }
      
      // If no search query, show all companies
      if (!this.searchQuery) {
        return this.companies;
      }
      
      // Filter companies based on search query
      const query = this.searchQuery.toLowerCase();
      return this.companies.filter(company => 
        company.company.nameTH.toLowerCase().includes(query) ||
        (company.company.nameEN && company.company.nameEN.toLowerCase().includes(query))
      );
    }
  },
  created() {
    this.$store.dispatch('companies/getList')
  },
  methods: {
    getDeviceRatio() {
      return window.devicePixelRatio
    },

  }
}
</script>

<style lang="scss">
* {
  font-family: "Noto Sans Thai", "Noto Sans", sans-serif;
}

.register-logo {
  width: 100px;
  display: block;
  margin: 20px auto;
}

#select-company {
  .v-list-item {
    border-bottom: 1px solid #c4c4c4;
  }

  .button-create {
    text-decoration: none !important;
    margin-right: 10px;
  }
}

</style>
